import React, { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getVoucherListing, getUserListing } from "../../utils/apiCalls";

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", width: "100%",
};
const btnPrimary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", color: C.white,
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const voucherStatuses = ["active", "redeemed", "expired", "cancelled"];
const sortableFields = ["createdAt", "expiresAt", "redeemedAt"];
const sortOrders = ["ASC", "DESC"];

const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        + ", " + date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true });
};

const StatusPill = ({ status }) => {
    switch (status) {
        case "active": return <Badge color={C.green} bg={C.greenLight}>● Active</Badge>;
        case "redeemed": return <Badge color={C.blue} bg={C.blueLight}>● Redeemed</Badge>;
        case "expired": return <Badge color={C.coral} bg={C.coralLight}>● Expired</Badge>;
        case "cancelled": return <Badge color={C.textLight} bg={C.borderLight}>● Cancelled</Badge>;
        default: return <Badge color={C.textMed} bg={C.borderLight}>{status}</Badge>;
    }
};

// What paid for the voucher: a stamps reward, or points spent from a percentage programme.
const VOUCHER_SOURCES = [
    { value: "stamps", label: "Stamps" },
    { value: "points", label: "Points" },
];

const SourcePill = ({ source }) =>
    source === "points"
        ? <Badge color={C.purple} bg={C.purpleLight}>Points</Badge>
        : <Badge color={C.orange} bg={C.orangeLight}>Stamps</Badge>;

const Vouchers = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [voucherData, setVoucherData] = useState([]);
    const [users, setUsers] = useState([]);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [tempLimit, setTempLimit] = useState(10);

    const initialFilters = {
        merchant: selectedMerchant || "",
        userId: "", status: "active", source: "", search: "",
        sortBy: "expiresAt", order: "ASC",
    };
    const [filters, setFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState(initialFilters);

    useEffect(() => {
        (async () => {
            const userList = await getUserListing({ limit: 1000 });
            const usersArray = userList?.users || [];
            setUsers(usersArray.filter((u) => u.userType === "Customer"));
        })();
    }, []);

    const fetchData = useCallback(async (page = 1) => {
        setIsLoading(true);
        const params = {
            ...filters,
            merchant: selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : undefined,
        };
        const ResData = await getVoucherListing(page, limit, params);
        if (ResData) {
            setVoucherData(ResData.vouchers || []);
            setTotalPages(ResData.pagination?.totalPages || 1);
        } else {
            setVoucherData([]);
            setTotalPages(1);
        }
        setIsLoading(false);
    }, [filters, limit, selectedMerchant]);

    useEffect(() => { fetchData(currentPage); }, [fetchData, currentPage]);

    useEffect(() => {
        setFilters((prev) => ({ ...prev, merchant: selectedMerchant || "" }));
        setTempFilters((prev) => ({ ...prev, merchant: selectedMerchant || "" }));
        setCurrentPage(1);
    }, [selectedMerchant]);

    useEffect(() => {
        if (filterModalOpen) { setTempFilters(filters); setTempLimit(limit); }
    }, [filterModalOpen, filters, limit]);

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) setCurrentPage(page);
    };
    const applyFilters = () => {
        setFilters(tempFilters); setLimit(tempLimit); setCurrentPage(1); setFilterModalOpen(false);
    };
    const clearFilters = () => {
        const cleared = { ...initialFilters, merchant: selectedMerchant || "" };
        setTempFilters(cleared); setFilters(cleared);
        setTempLimit(10); setLimit(10); setCurrentPage(1); setFilterModalOpen(false);
    };

    const tiles = useMemo(() => {
        const total = voucherData.length;
        const active = voucherData.filter((v) => v.status === "active").length;
        const redeemed = voucherData.filter((v) => v.status === "redeemed").length;
        const expired = voucherData.filter((v) => v.status === "expired").length;
        return { total, active, redeemed, expired };
    }, [voucherData]);

    const activeFilterPills = useMemo(() => {
        const pills = [];
        const { userId, status, source, search } = filters;
        if (status && status !== "active") pills.push({ key: "status", label: "Status", value: status.charAt(0).toUpperCase() + status.slice(1) });
        if (source) pills.push({ key: "source", label: "Source", value: VOUCHER_SOURCES.find((s) => s.value === source)?.label || source });
        if (userId) {
            const u = users.find((x) => x.id === parseInt(userId));
            pills.push({ key: "userId", label: "User", value: u ? `${u.firstName} ${u.lastName}` : userId });
        }
        if (search) pills.push({ key: "search", label: "Code", value: search });
        return pills;
    }, [filters, users]);

    const removeFilter = (key) => {
        const next = { ...filters };
        if (key === "status") next.status = "active";
        else if (key === "source") next.source = "";
        else if (key === "userId") next.userId = "";
        else if (key === "search") next.search = "";
        setFilters(next); setCurrentPage(1);
    };

    const renderPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        return pages;
    };
    const pageBtnStyle = (active, disabled) => ({
        minWidth: 36, height: 36, padding: "0 12px", borderRadius: 8,
        border: `1px solid ${active ? C.coral : C.border}`,
        background: active ? `linear-gradient(135deg, ${C.coral}, #00bfa5)` : C.white,
        color: active ? C.white : C.textMed,
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, transition: "all 0.15s",
    });

    const activeFilterCount = activeFilterPills.length;

    return (
        <Fragment>
            <Breadcrumb title="Vouchers" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* Tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Vouchers (page)" value={tiles.total} sub={`Page ${currentPage} of ${totalPages}`} icon="🎟️" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Active" value={tiles.active} sub="Ready to redeem" icon="●" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                        <Tile label="Redeemed" value={tiles.redeemed} sub="On this page" icon="✓" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={160} />
                        <Tile label="Expired" value={tiles.expired} sub="No longer usable" icon="⌛" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={240} />
                    </div>

                    {/* Table */}
                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>🎟️ Vouchers <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} on this page</span></span>
                                <button onClick={() => setFilterModalOpen(true)} style={btnPrimary}>
                                    Filters {activeFilterCount > 0 && <span style={{ background: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 10, marginLeft: 6, fontSize: 11 }}>{activeFilterCount}</span>}
                                </button>
                            </div>
                        }
                    >
                        {/* Active filter pills */}
                        {activeFilterPills.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                                {activeFilterPills.map((p) => (
                                    <span
                                        key={p.key}
                                        onClick={() => removeFilter(p.key)}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: 6,
                                            padding: "5px 10px", borderRadius: 8, fontSize: 12,
                                            background: C.coralLight, color: C.coralDark, fontWeight: 600,
                                            cursor: "pointer", border: `1px solid #e0f2f1`,
                                        }}
                                        title="Click to remove"
                                    >
                                        <strong>{p.label}:</strong> {p.value} <span style={{ opacity: 0.6 }}>×</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {isLoading ? (
                            <Loader />
                        ) : voucherData.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No vouchers found.</div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={tbl.table}>
                                        <thead>
                                            <tr>
                                                <th style={tbl.th}>Code</th>
                                                <th style={tbl.th}>Merchant</th>
                                                <th style={tbl.th}>User</th>
                                                <th style={tbl.th}>Product</th>
                                                <th style={tbl.th}>Source</th>
                                                <th style={tbl.th}>Status</th>
                                                <th style={tbl.th}>Redeemed</th>
                                                <th style={tbl.th}>POS Ref</th>
                                                <th style={tbl.th}>Expires</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {voucherData.map((v) => (
                                                <tr key={v.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, fontWeight: 700, color: C.text }}>
                                                        {v.code}
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed }}>{v.merchant?.name || "—"}</td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>{v.user?.name || "—"}</td>
                                                    <td style={tbl.td}>{v.product?.name || "—"}</td>
                                                    <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                                                        <SourcePill source={v.source} />
                                                        {v.source === "points" && v.pointsCost != null && (
                                                            <span style={{ marginLeft: 6, color: C.textMed, fontSize: 12 }}>{v.pointsCost} pts</span>
                                                        )}
                                                        {(v.redeemChannel === "app" || v.redeemChannel === "pos") && (
                                                            <div style={{ marginTop: 4, color: C.textLight, fontSize: 11 }}>
                                                                {v.redeemChannel === "app" ? "App only" : "Store only"}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={tbl.td}><StatusPill status={v.status} /></td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>{v.redeemedAt ? formatDate(v.redeemedAt) : <span style={{ color: C.textLight }}>—</span>}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}>{v.posTransactionRef || <span style={{ color: C.textLight }}>—</span>}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>{v.expiresAt ? formatDate(v.expiresAt) : <span style={{ color: C.textLight }}>—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
                                        <button style={pageBtnStyle(false, currentPage <= 1)} disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                                        {renderPagination().map((p) => (
                                            <button key={p} style={pageBtnStyle(p === currentPage, false)} onClick={() => handlePageChange(p)}>{p}</button>
                                        ))}
                                        <button style={pageBtnStyle(false, currentPage >= totalPages)} disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>›</button>
                                    </div>
                                )}
                            </>
                        )}
                    </RCard>
                </div>
            </Container>

            {/* Filter Modal */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Vouchers</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>USER</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.userId} onChange={(e) => setTempFilters({ ...tempFilters, userId: e.target.value })}>
                                <option value="">All Users</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.firstName} {u.lastName} ({u.phoneNo})
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>STATUS</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.status} onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}>
                                <option value="">All Statuses</option>
                                {voucherStatuses.map((s) => (
                                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SOURCE</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.source} onChange={(e) => setTempFilters({ ...tempFilters, source: e.target.value })}>
                                <option value="">Stamps &amp; Points</option>
                                {VOUCHER_SOURCES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>VOUCHER CODE</Label>
                            <Input type="text" style={inputStyle} value={tempFilters.search} onChange={(e) => setTempFilters({ ...tempFilters, search: e.target.value })} placeholder="Enter voucher code" />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>SORT BY</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.sortBy} onChange={(e) => setTempFilters({ ...tempFilters, sortBy: e.target.value })}>
                                {sortableFields.map((f) => (
                                    <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1).replace(/([A-Z])/g, " $1")}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>ORDER</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.order} onChange={(e) => setTempFilters({ ...tempFilters, order: e.target.value })}>
                                {sortOrders.map((o) => <option key={o} value={o}>{o}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>PER PAGE</Label>
                            <Input type="select" style={inputStyle} value={tempLimit} onChange={(e) => setTempLimit(parseInt(e.target.value))}>
                                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={clearFilters} style={btnSecondary}>Clear</button>
                    <button onClick={applyFilters} style={btnPrimary}>Apply</button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default Vouchers;
